import React, { useState } from 'react';
import Icon from '../Icon';
import Button from '../ui/Button';
import DeliveryBadge from './DeliveryBadge';
import ProviderNotice from './ProviderNotice';
import { useIdempotencyKey } from './useIdempotencyKey';
import { formatDate, formatTime } from '../../utils/formatDate';
import { replyTarget, type InboxThread } from '../../utils/inboxThreads';
import { sameCalendarDay } from './formatMessageTime';
import type { InboxMessage } from '../../types/Communications';

interface ConversationPaneProps {
  thread: InboxThread;
  /** Planner and above. A viewer reads only: no reply, mute or delete. */
  canEdit: boolean;
  /** WhatsApp is not set up for this project: sending is switched off. */
  sendBlocked: boolean;
  onSend: (thread: InboxThread, text: string, idempotencyKey: string) => Promise<void>;
  onToggleMute: (thread: InboxThread) => Promise<void>;
  onDelete: (message: InboxMessage) => void;
  onBack: () => void;
}

const ConversationPane: React.FC<ConversationPaneProps> = ({
  thread, canEdit, sendBlocked, onSend, onToggleMute, onDelete, onBack,
}) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [muting, setMuting] = useState(false);
  const idem = useIdempotencyKey();

  const target = replyTarget(thread);
  const canReply = canEdit && target !== null;
  const chronological = [...thread.items].reverse();
  const followUp = thread.latest.followup_date;

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending || sendBlocked) return;
    setSending(true);
    setError('');
    try {
      await onSend(thread, trimmed, idem.keyFor(`${thread.key}\u0000${trimmed}`));
      idem.reset();
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const toggleMute = async () => {
    setMuting(true);
    try { await onToggleMute(thread); } finally { setMuting(false); }
  };

  return (
    <section className="communications-detail" aria-label={`Conversation with ${thread.label}`}>
      <header className="communications-detail-header">
        <button type="button" className="communications-back" onClick={onBack} aria-label="Back to conversations">
          <Icon name="arrowLeft" size={16} />
        </button>
        <span className="communications-avatar" aria-hidden="true">{thread.label.slice(0, 1).toUpperCase()}</span>
        <div className="communications-detail-header-text">
          <h2>{thread.label}</h2>
          <span>{thread.items.length} message{thread.items.length === 1 ? '' : 's'}</span>
        </div>
        {thread.contact && <span className="ds-badge ds-badge-primary">Guest</span>}
        {thread.isWhatsApp && <span className="ds-badge ds-badge-whatsapp">WhatsApp</span>}
        {thread.latest.priority === 'urgent' && <span className="ds-badge ds-badge-danger">Urgent</span>}
        {thread.muted && <span className="ds-badge ds-badge-neutral">Muted</span>}
        {canEdit && thread.threadKey && (
          <Button variant="secondary" size="sm" onClick={toggleMute} loading={muting}
            icon={<Icon name={thread.muted ? 'bell' : 'eyeOff'} size={14} />}>
            {thread.muted ? 'Unmute' : 'Mute'}
          </Button>
        )}
      </header>

      {followUp && (
        <div className="communications-detail-followup">
          <Icon name="calendar" size={14} />
          Follow-up due {formatDate(followUp)}
        </div>
      )}

      <div className="communications-detail-messages">
        {chronological.map((msg, idx) => {
          const prev = chronological[idx - 1];
          const outbound = msg.direction === 'outbound';
          return (
            <React.Fragment key={msg.id}>
              {(!prev || !sameCalendarDay(prev.created_at, msg.created_at)) && (
                <div className="communications-date-pill-row">
                  <span className="communications-date-pill">{formatDate(msg.created_at)}</span>
                </div>
              )}
              <div className={`communications-bubble-row communications-bubble-row--${outbound ? 'out' : 'in'}`}>
                <div className={`communications-bubble communications-bubble--${outbound ? 'out' : 'in'}`}>
                  <div className="communications-bubble-meta">
                    <span className="communications-bubble-sender">{outbound ? 'You' : (msg.contact?.name || thread.label)}</span>
                    <span className="communications-bubble-time">{formatTime(msg.created_at, '')}</span>
                  </div>
                  <div className="communications-bubble-body">{msg.body || msg.subject}</div>
                  <DeliveryBadge message={msg} />
                  {canEdit && (
                    <button type="button" className="communications-bubble-delete" aria-label="Delete message" title="Delete message" onClick={() => onDelete(msg)}>
                      <Icon name="trash" size={13} />
                    </button>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {canReply && (
        <div className="communications-detail-reply">
          {sendBlocked && <ProviderNotice />}
          {error && <div className="communications-error" role="alert">{error}</div>}
          <textarea
            className="form-control"
            rows={2}
            aria-label="Reply"
            placeholder="Type your reply…"
            value={text}
            onChange={e => setText(e.target.value)}
            disabled={sending}
          />
          <Button variant="primary" onClick={send} loading={sending}
            disabled={sendBlocked || !text.trim()} icon={<Icon name="send" size={16} />}>
            Send reply
          </Button>
        </div>
      )}
    </section>
  );
};

export default ConversationPane;
